import { useState } from 'react';
import { getInitials } from '../utils/helper';

/**
 * Avatar — a user/client photo with a graceful initials fallback.
 *
 * Until the Supabase avatar bucket + `avatarUrl` are wired end-to-end, `src`
 * will simply be empty and this renders brand-tinted initials — so it is safe
 * to drop in everywhere a person/client appears right now. The moment the API
 * starts returning `avatarUrl`, the same call sites show the real photo with
 * no further change.
 *
 * Props:
 *   src       — image URL (optional; falls back to initials)
 *   name      — full name, used for initials + alt/title
 *   size      — px (number), default 36
 *   square    — rounded-square instead of circle (matches the client cards)
 *   className — extra class(es)
 */
const Avatar = ({ src, name = '', size = 36, square = false, className = '' }) => {
    // A URL that 404s/410s (deleted upload, expired signed link) would otherwise
    // render as the browser's broken-image glyph, so we remember the one that
    // failed and drop through to the initials below. Keyed on the URL itself so
    // a later, working `src` still gets a chance to load.
    const [failedSrc, setFailedSrc] = useState('');
    const dim = { width: size, height: size };
    const shape = square ? ' sbr-avatar-sq' : '';

    if (src && src !== failedSrc) {
        return (
            <img
                className={`sbr-avatar${shape} ${className}`.trim()}
                style={dim}
                src={src}
                alt={name}
                title={name}
                onError={() => setFailedSrc(src)}
            />
        );
    }

    return (
        <span
            className={`sbr-avatar sbr-avatar-fallback${shape} ${className}`.trim()}
            // The initials size rides on a custom property rather than an inline
            // font-size so a host chip class (.row-sbr .a, .arow .av, …) can still
            // win on specificity and keep its own type scale.
            style={{ ...dim, '--sbr-avatar-fs': `${Math.max(10, Math.round(size * 0.38))}px` }}
            title={name}
            aria-label={name || 'avatar'}
        >
            {getInitials(name) || '–'}
        </span>
    );
};

export default Avatar;
